import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Leaderboard from "@/pages/Leaderboard";
import Projects from "@/pages/Projects";
import DataInconsistency from "@/pages/DataInconsistency";
import Forum from "@/pages/Forum";
import ComplianceData from "@/pages/ComplianceData";
import Revisions from "@/pages/Revisions";
import Training from "@/pages/Training";
import Meetings from "@/pages/Meetings";
import Reports from "@/pages/Reports";
import People from "@/pages/People";
import Verification from "@/pages/Verification";
import Activity from "@/pages/Activity";
import InvoiceTracker from "@/pages/InvoiceTracker";
import Cockpit from "@/pages/Cockpit";
import Approvers from "@/pages/Approvers";
import ComingSoon from "@/pages/ComingSoon";
import DataFiles from "@/pages/DataFiles";
import NoticeBoard from "@/pages/NoticeBoard";
import Library from "@/pages/Library";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import SetPassword from "@/pages/SetPassword";

const Splash = () => (
  <div className="flex h-screen items-center justify-center bg-background">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

/** A screen SDTD has taken away says so once, then puts the officer back on the Home page. */
const Refused = () => {
  useEffect(() => {
    toast.error("That screen is not open to you. SDTD decides who sees what.");
  }, []);
  return <Navigate to="/" replace />;
};

// The words an officer knows each tab by, for the curtain page.
const SCREEN_LABELS = {
  dashboard: "The Dashboard",
  leaderboard: "The Leaderboard",
  projects: "The register",
  inconsistency: "Data Inconsistency",
  forum: "The Discussion Forum",
  compliance_data: "Compliance Data",
  revisions: "Date Revisions",
  training: "Training Status",
  meetings: "Meeting Cadence",
  reports: "Review packs",
  people: "The team directory",
  verification: "The approval queue",
  activity: "The activity trail",
  invoices: "The Invoice Tracker",
  data: "Data files",
  library: "Important Files",
  users: "Users",
  settings: "Reminders & digest",
};

const Protected = ({ children, adminOnly, uploaderOnly, allowIncomplete, screen }) => {
  const { user, loading, isAdmin, canUpload, profileComplete, passwordChangeRequired, sees,
    curtained, cockpitOwner, superAdminEligible } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  // Signed in with the password the portal gave out — nothing else until they set their own.
  if (passwordChangeRequired) return <Navigate to="/set-password" replace />;
  // Nobody moves inside the portal until the joining details are filled.
  if (!profileComplete && !allowIncomplete) return <Navigate to="/profile" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  if (uploaderOnly && !canUpload) return <Navigate to="/" replace />;
  // The control cockpit belongs to one officer; for anyone else the address does not exist.
  if (screen === "cockpit" && !cockpitOwner) return <Navigate to="/" replace />;
  // The Approvers table belongs to the two SDTD administrators.
  if (screen === "approvers" && !superAdminEligible) return <Navigate to="/" replace />;
  // So does the notice board — its band is read by everyone, written by them alone.
  if (screen === "board" && !superAdminEligible) return <Navigate to="/" replace />;
  // A screen this role may not reach cannot be opened by typing its address either.
  if (screen && !["cockpit", "approvers", "board"].includes(screen) && !sees(screen))
    return <Refused />;
  // Built, but SDTD has not opened it yet. The tab stays; the screen says so.
  if (curtained(screen))
    return (
      <AppShell>
        <ComingSoon screen={screen} label={SCREEN_LABELS[screen]} />
      </AppShell>
    );
  return <AppShell>{children}</AppShell>;
};

const SetPasswordRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <SetPassword />;
};

const LoginRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  return user ? <Navigate to="/" replace /> : <Login />;
};

const ROUTES = [
  ["/", <Dashboard />, false, false, false, "dashboard"],
  ["/dashboard", <Dashboard />, false, false, false, "dashboard"],
  ["/leaderboard", <Leaderboard />, false, false, false, "leaderboard"],
  ["/projects", <Projects />, false, false, false, "projects"],
  ["/inconsistency", <DataInconsistency />, false, false, false, "inconsistency"],
  ["/forum", <Forum />, false, false, false, "forum"],
  ["/forum/:threadId", <Forum />, false, false, false, "forum"],
  ["/compliance-data", <ComplianceData />, false, false, false, "compliance_data"],
  ["/revisions", <Revisions />, false, false, false, "revisions"],
  ["/training", <Training />, false, false, false, "training"],
  ["/meetings", <Meetings />, false, false, false, "meetings"],
  ["/reports", <Reports />, false, false, false, "reports"],
  ["/people", <People />, false, false, false, "people"],
  ["/verification", <Verification />, false, false, false, "verification"],
  ["/activity", <Activity />, false, false, false, "activity"],
  ["/invoices", <InvoiceTracker />, false, false, false, "invoices"],
  ["/cockpit", <Cockpit />, false, false, false, "cockpit"],
  ["/approvers", <Approvers />, false, false, false, "approvers"],
  ["/board", <NoticeBoard />, false, false, false, "board"],
  ["/profile", <Profile />, false, false, true, "profile"],
  ["/data", <DataFiles />, false, false, false, "data"],
  ["/library", <Library />, false, false, false, "library"],
  ["/admin/settings", <Settings />, true, false, false, "settings"],
];

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/set-password" element={<SetPasswordRoute />} />
          {ROUTES.map(([path, element, adminOnly, uploaderOnly, allowIncomplete, screen]) => (
            <Route
              key={path}
              path={path}
              element={
                <Protected
                  adminOnly={adminOnly}
                  uploaderOnly={uploaderOnly}
                  allowIncomplete={allowIncomplete}
                  screen={screen}
                >
                  {element}
                </Protected>
              }
            />
          ))}
          {/* The three tabs that were folded into the Dashboard and the Compliance Data screen —
              an old address, a bookmark or a link in a mail still lands somewhere sensible. */}
          <Route path="/home-page" element={<Navigate to="/" replace />} />
          <Route path="/admin/users" element={<Navigate to="/people" replace />} />
          <Route path="/current-fy" element={<Navigate to="/" replace />} />
          <Route path="/deep-dive" element={<Navigate to="/compliance-data" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
